import { Immutable } from "immer";
import React from "react";
import Modal from "react-modal";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { isoAPR, isoMonths } from "../../models/nominals";
import { IServiceFooterValueProps } from "../../models/value-props";
import { listFinancingPlans } from "../../utils/financing";
import { focusElement } from "../../utils/keyboardFocus";
import { ServiceFooterValuePropsModalContent } from "../ServiceFooterValuePropsModalContent";

const getBestFinancingPlan = () => {
    const plans = listFinancingPlans();
    const bestPlan = plans.length > 0 ? plans[plans.length - 1] : null;
    let planAPR = isoAPR.wrap("0");
    let planLength = isoMonths.wrap(0);
    if (bestPlan) {
        planAPR = bestPlan.apr;
        planLength = bestPlan.length;
    }
    return { planAPR, planLength };
};

const getModalDimensions = () => {
    const window = getWindow();
    let modalWidth = "";
    let modalPadding = "";
    const innerWidth = window ? window.innerWidth : 1024;
    if (innerWidth < 500) {
        modalWidth = "380px";
        modalPadding = "40px 15px 15px 15px";
    } else if (innerWidth < 768) {
        modalWidth = "85%";
        modalPadding = "50px 20px 20px 20px";
    } else {
        modalWidth = "66.666%";
        modalPadding = "30px";
    }
    return { modalWidth, modalPadding };
};

export const ServiceFooterValuePropsModal = (
    props: Immutable<{
        content: IServiceFooterValueProps;
    }> & {
        isOpen: boolean;
        onClose: (
            event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
        ) => void;
    },
): React.ReactNode => {
    const [contentHeight, setContentHeight] = React.useState(500);
    const contentElemRef = React.useRef<HTMLDivElement>(null);
    const heightCalcDelayTimerRef = React.useRef<number | undefined>(undefined);

    const { planAPR, planLength } = getBestFinancingPlan();
    const { modalWidth, modalPadding } = getModalDimensions();

    console.log(`Content Height ${contentHeight}`);

    const modalStyleProps = {
        content: {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            margin: "auto",
            width: modalWidth,
            height: `${contentHeight + 55}px`,
            padding: modalPadding,
            overflow: "hidden",
            boxShadow: "none",
        },
    };

    const handleAfterOpen = () => {
        const window = getWindow();
        // Timeout currently used to mitigate miscalculation of height on first load
        // TODO: Figure out why height is correct on second load and remove timoe
        if (window) {
            heightCalcDelayTimerRef.current = window.setTimeout(() => {
                if (contentElemRef.current) {
                    setContentHeight(
                        contentElemRef.current.getBoundingClientRect().height,
                    );
                }
            }, 100);
        }
        focusElement(".service-footer-modal__close");
    };

    const handleRequestClose = (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ) => {
        const window = getWindow();
        if (window) {
            window.clearTimeout(heightCalcDelayTimerRef.current);
        }
        props.onClose(event);
    };

    return (
        <Modal
            onAfterOpen={handleAfterOpen}
            contentLabel={t`Buy Direct For Exclusive Benefits`}
            aria={{ modal: true }}
            style={modalStyleProps}
            isOpen={props.isOpen}
            onRequestClose={handleRequestClose}
            role="dialog"
            overlayClassName={"service-footer-container__react-modal-overlay"}
            className={"service-footer-container__react-modal-content"}
        >
            <div ref={contentElemRef}>
                <ServiceFooterValuePropsModalContent
                    onCloseModal={props.onClose}
                    apr={planAPR}
                    length={planLength}
                    content={props.content}
                />
            </div>
        </Modal>
    );
};
